import { ReactNode } from "react";
import Skeleton from "react-loading-skeleton";
import { formatToNumber } from "../../helperFunctions";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { Button, ButtonVariations } from "../buttons/button";
import { Icon } from "../icons/icon";

export const NumberCard = ({
  title,
  value,
  className = "",
  iconClassName = "",
  titleClassName = "",
  valueClassName = "",
  icon = "coins",
  children,
  primaryButtonText = "",
  onPrimaryButtonClick,
  primaryButtonVariant = "primary-white",
  primaryButtonIcon = "plus",
  primaryButtonDisabled = false,
  secondaryButtonText = "",
  onSecondaryButtonClick,
  secondaryButtonVariant = "secondary-white",
  secondaryButtonIcon = "arrow-up",
  secondaryButtonDisabled = false,
  loading = false,
  isCurrency = true,
}: {
  title: string;
  value?: string | number;
  className?: string;
  iconClassName?: string;
  titleClassName?: string;
  valueClassName?: string;
  icon?: string;
  children?: ReactNode;
  primaryButtonText?: string;
  onPrimaryButtonClick?: () => void;
  primaryButtonVariant?: ButtonVariations;
  primaryButtonIcon?: string;
  primaryButtonDisabled?: boolean;
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  secondaryButtonVariant?: ButtonVariations;
  secondaryButtonIcon?: string;
  secondaryButtonDisabled?: boolean;
  loading?: boolean;
  isCurrency?: boolean;
}) => {
  return (
    <div
      className={`
        rounded-lg border border-bg-pry-dark-green-300/10 
        bg-pry-green-200 px-6 py-4 text-white
        min-h-[120px] flex flex-col md:flex-row flex-wrap gap-4
        md:items-center justify-center md:justify-between w-full max-w-[604px]
        ${className}
        `}
    >
      <div className="flex-1 flex flex-col gap-2 max-h-fit">
        <div className="flex flex-row flex-wrap gap-4 items-center">
          <div
            className={`p-1 bg-white w-fit rounded-[4px] text-pry-green-200 ${iconClassName}`}
          >
            <Icon svg={icon} width={24} height={24} className="" />
          </div>

          <p className="flex flex-col gap-1 flex-1">
            {loading ? (
              <Skeleton
                width={105}
                height={20}
                baseColor={SKELETON_BASE_COLOR}
                highlightColor={SKELETON_HIGHLIGHT_COLOR}
                className="opacity-50"
              />
            ) : (
              <span
                className={`text-xs md:text-sm font-medium ${titleClassName}`}
              >
                {title}
              </span>
            )}

            {loading ? (
              <Skeleton
                width={"100%"}
                height={32}
                baseColor={SKELETON_BASE_COLOR}
                highlightColor={SKELETON_HIGHLIGHT_COLOR}
                containerClassName="w-full"
                className="opacity-50"
              />
            ) : (
              <span
                className={`text-xl md:text-2xl font-bold ${valueClassName}`}
              >
                {formatToNumber(value ?? 0, {
                  decimalPlaces: 2,
                  currency: "NGN",
                  style: isCurrency ? "currency" : "decimal",
                })}
              </span>
            )}
          </p>
        </div>

        {!loading ? children : undefined}
      </div>

      {(onPrimaryButtonClick || onSecondaryButtonClick) && !loading ? (
        <div className="w-full md:w-fit md:min-w-[180px] flex md:flex-col gap-[10px]">
          {onPrimaryButtonClick && (
            <Button
              text={primaryButtonText}
              onClick={() => {
                onPrimaryButtonClick();
              }}
              type="button"
              className="!py-2 md:!py-[12px] !px-5 !text-xs md:!text-sm flex-1"
              loading={false}
              disabled={primaryButtonDisabled}
              icon={primaryButtonIcon}
              variation={primaryButtonVariant}
            />
          )}

          {onSecondaryButtonClick && (
            <Button
              text={secondaryButtonText}
              onClick={() => {
                onSecondaryButtonClick();
              }}
              type="button"
              className="!py-2 md:!py-[12px] !px-5 !text-xs md:!text-sm flex-1"
              loading={false}
              disabled={secondaryButtonDisabled}
              icon={secondaryButtonIcon}
              variation={secondaryButtonVariant}
            />
          )}
        </div>
      ) : undefined}
    </div>
  );
};
